/**
 * Topic duration history for planner suggestions (last 3 sessions + average).
 * Fetches from GET /session/topic-duration-history/.
 */

import { authService } from '../../../utils/authService';

export interface TopicSessionDurationRecord {
  sessionLabel: string;
  actualMinutes: number;
  date: string;
  session_id?: string;
}

export interface TopicDurationHistoryEntry {
  title: string;
  averageMinutes: number | null;
  lastSessions: TopicSessionDurationRecord[];
}

type CacheBucket = {
  fetchedAt: number;
  byTitle: Record<string, TopicDurationHistoryEntry>;
};

const CACHE_TTL_MS = 5 * 60 * 1000;
const cacheByCourse = new Map<string, CacheBucket>();

function normalizeKey(title: string): string {
  return String(title || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function cacheKey(courseId?: string): string {
  return String(courseId || '').trim() || '_all';
}

export function isTopicDurationUnset(minutes: number | null | undefined): boolean {
  // 0 must count as unset — Add to Path seeds durationMinutes: 0 until the trainer sets time.
  return minutes == null || !Number.isFinite(Number(minutes)) || Number(minutes) <= 0;
}

function findEntryInBucket(
  bucket: CacheBucket,
  topicTitle: string,
  key: string,
): TopicDurationHistoryEntry | undefined {
  return (
    bucket.byTitle[topicTitle] ||
    Object.values(bucket.byTitle).find(
      (e: TopicDurationHistoryEntry) => normalizeKey(e.title) === key,
    )
  );
}

export function getTopicSessionHistory(topicTitle: string): TopicSessionDurationRecord[] {
  const key = normalizeKey(topicTitle);
  const buckets = Array.from(cacheByCourse.values());
  for (let i = 0; i < buckets.length; i += 1) {
    const entry = findEntryInBucket(buckets[i], topicTitle, key);
    if (entry?.lastSessions?.length) return entry.lastSessions;
  }
  return [];
}

export function getTopicLastSessionDurations(
  topicTitle: string,
  count = 3,
): TopicSessionDurationRecord[] {
  return getTopicSessionHistory(topicTitle).slice(0, count);
}

export function getTopicAverageDurationMinutes(topicTitle: string): number | null {
  const key = normalizeKey(topicTitle);
  const buckets = Array.from(cacheByCourse.values());
  for (let i = 0; i < buckets.length; i += 1) {
    const entry = findEntryInBucket(buckets[i], topicTitle, key);
    if (entry && entry.averageMinutes != null) return entry.averageMinutes;
  }
  const history = getTopicSessionHistory(topicTitle);
  if (history.length === 0) return null;
  const total = history.reduce((sum, row) => sum + row.actualMinutes, 0);
  return Math.round(total / history.length);
}

/**
 * Prefetch last-N durations for a set of topic titles (e.g. Active Learning Path).
 * Always refreshes missing titles; refreshes whole set when cache TTL expires.
 */
export async function prefetchTopicDurationHistory(
  titles: string[],
  courseId?: string,
): Promise<Record<string, TopicDurationHistoryEntry>> {
  const unique = Array.from(
    new Set(titles.map((t) => String(t || '').trim()).filter(Boolean)),
  );
  if (unique.length === 0) return {};

  const ck = cacheKey(courseId);
  const existing = cacheByCourse.get(ck);
  const now = Date.now();
  const cacheFresh = Boolean(existing && now - existing.fetchedAt < CACHE_TTL_MS);
  const missing = unique.filter((t) => !existing?.byTitle?.[t]);
  if (cacheFresh && missing.length === 0) {
    return existing!.byTitle;
  }

  const toFetch = cacheFresh ? missing : unique;

  try {
    const params = new URLSearchParams();
    params.set('titles', toFetch.join(','));
    if (courseId) params.set('course_id', courseId);
    params.set('limit', '3');
    const res = await authService.get(
      `/session/topic-duration-history/?${params.toString()}`,
    );
    const topics = (res?.data?.topics || {}) as Record<
      string,
      {
        title?: string;
        averageMinutes?: number | null;
        lastSessions?: TopicSessionDurationRecord[];
      }
    >;
    const byTitle: Record<string, TopicDurationHistoryEntry> = {
      ...(existing?.byTitle || {}),
    };
    for (const title of toFetch) {
      const row =
        topics[title] ||
        Object.values(topics).find(
          (t) => normalizeKey(String(t.title || '')) === normalizeKey(title),
        ) ||
        {};
      const lastSessions = Array.isArray(row.lastSessions)
        ? row.lastSessions.slice(0, 3)
        : [];
      let averageMinutes =
        row.averageMinutes != null ? Number(row.averageMinutes) : null;
      if (averageMinutes == null && lastSessions.length > 0) {
        const total = lastSessions.reduce((sum, s) => sum + s.actualMinutes, 0);
        averageMinutes = Math.round(total / lastSessions.length);
      }
      byTitle[title] = {
        title,
        averageMinutes,
        lastSessions,
      };
    }
    cacheByCourse.set(ck, {
      fetchedAt: cacheFresh && existing ? existing.fetchedAt : now,
      byTitle,
    });
    return byTitle;
  } catch (err) {
    console.error('prefetchTopicDurationHistory failed', err);
    return existing?.byTitle || {};
  }
}
